/**
 * SideMenuScoreSection — Web 侧边栏 Fitness Score 信息区
 *
 * Figma: `97318:91502` → `Fitness Score (nav)` 节点
 * 在 SideMenu 主导航下方展示迷你雷达图 + 总分 + chevron，点击跳转 /my。
 *
 * 技术选择：
 * - 40×40 迷你雷达 placeholder（L-phase 替换为实时 SVG）
 * - 分数格式 "72/100"
 * - 无 score 时不渲染
 */

import { I18nUtil } from "@utils/i18n";
import { Icon } from "@ospweb/sdk-ui";
import Translations from "@translate/components/fitness/sidebar/SideMenuScoreSection";
import { buildOpenReplayLabel } from "@/src/utils/openreplay";
import { AppImage } from "@/src/components/NextImage";

const I18n = I18nUtil.createScoped(Translations);

export interface SideMenuScoreSectionProps {
  score: number | null;
  isLoading: boolean;
  onSeeDetails: () => void;
}

/**
 * 侧边栏 Score 信息区 — 迷你雷达 + 分数 + chevron
 */
export function SideMenuScoreSection({
  score,
  isLoading,
  onSeeDetails,
}: SideMenuScoreSectionProps) {
  const showPlaceholder = isLoading || score === null;

  return (
    <button
      type="button"
      className="flex gap-2 items-center overflow-clip rounded-2 bg-background_solid_neutral_primary px-3 py-2 w-full text-left"
      data-testid="sidemenu-score-section"
      data-openreplay-label={buildOpenReplayLabel(
        "Fitness",
        "SideMenu",
        "Click",
        "See score details",
      )}
      onClick={onSeeDetails}
    >
      <div className="shrink-0 size-10">
        <AppImage
          alt=""
          aria-hidden
          src="/assets/images/fitness/radar-mini.webp"
          className="size-full object-contain"
          decoding="async"
        />
      </div>

      <div className="flex flex-col items-start flex-1 min-w-0 overflow-clip">
        <p className="body_medium_s text-content_secondary whitespace-nowrap mb-[-1px]">
          {I18n.t("Financial fitness score")}
        </p>
        <div className="flex gap-0.5 items-end">
          {showPlaceholder ? (
            <span className="title_xl text-content_quaternary">--</span>
          ) : (
            <>
              <span className="title_xl text-content_primary">{score}</span>
              <span className="body_medium_s text-content_quaternary py-1">
                /100
              </span>
            </>
          )}
        </div>
      </div>

      <Icon
        name="chevron-right"
        size={20}
        className="shrink-0 text-content_quaternary"
      />
    </button>
  );
}
